<script lang="ts">
  import Badge from '../lib/ui/Badge.svelte';
  import Button from '../lib/ui/Button.svelte';
  import Card from '../lib/ui/Card.svelte';
  import AddTenantDrawer from './AddTenantDrawer.svelte';
  import ReportLineDrawer from './ReportLineDrawer.svelte';
  import ReportSendsPage from './ReportSendsPage.svelte';
  import { Format } from '../lib/format';
  import { ReportChannel } from '../lib/report-channel';
  import { ReportDefinitionStatus } from '../lib/report-definition-status';
  import { ReportLineTenant } from '../lib/report-line-tenant';
  import { ReportSchedule } from '../lib/report-schedule';
  import type { ReportDefinitionGroup, ReportDefinitionTenant } from '../lib/api';
  import type { ReportDefinitionsBoard } from '../lib/report-definitions-board.svelte';

  interface Props {
    board: ReportDefinitionsBoard;
    group: ReportDefinitionGroup;
  }

  let { board, group }: Props = $props();

  const lines = $derived(board.linesOf(group));
  const now = new Date();
  let addingTenant = $state(false);
  let newTenant = $state<ReportDefinitionTenant | null>(null);

  function chooseTenant(tenant: ReportDefinitionTenant): void {
    addingTenant = false;
    newTenant = tenant;
  }
</script>

{#if board.isShowingSends && board.openedLine}
  {#key board.openedLine.id}
    <ReportSendsPage {board} {group} definition={board.openedLine} />
  {/key}
{:else}
<header class="detail-header">
  <div class="back"><Button size="sm" onclick={() => board.backToGroups()}>← Relatórios</Button></div>
  <h1>{group.name}</h1>
  <div class="channels">
    {#each group.channels as channel (channel)}
      <Badge tone="info">{ReportChannel.label(channel)}</Badge>
      {#if channel === 'whatsapp'}<span class="sub">modelo <span class="mono">{group.templateName}</span></span>{/if}
    {/each}
  </div>
</header>

<Card title="Clientes" count={lines.length}>
  {#snippet actions()}
    <Button size="sm" variant="primary" onclick={() => (addingTenant = true)}>+ Adicionar cliente</Button>
  {/snippet}
  <div class="rows">
    <div class="row head">
      <span class="label">Cliente</span><span class="label">Quando sai</span><span class="label">Contatos</span><span class="label"
        >Situação</span
      >
    </div>
    {#each lines as line (line.id)}
      {@const schedule = new ReportSchedule(line.schedule)}
      {@const status = ReportDefinitionStatus.of(line.status)}
      <button class="row clickable" class:paused={line.status === 'paused'} onclick={() => board.openLine(line)}>
        <span class="name">{ReportLineTenant.of(line).name}</span>
        <span class="who">
          <span class="name">{schedule.describe()}</span>
          <span class="sub">Próximo: {schedule.nextSend(now)?.label ?? 'sem data'}</span>
        </span>
        <span>{Format.quantity(line.recipients.length, { one: 'contato', many: 'contatos' })}</span>
        <Badge tone={status.tone} dot>{status.label}</Badge>
      </button>
    {:else}
      <p class="empty">Nenhum cliente neste relatório ainda.</p>
    {/each}
  </div>
  <p class="footnote">Cada cliente tem o próprio horário; o contato recebe em todo canal do relatório em que tem cadastro</p>
</Card>

{#if addingTenant}
  <AddTenantDrawer {board} {group} onChoose={chooseTenant} onClose={() => (addingTenant = false)} />
{:else if newTenant}
  {#key newTenant.tenantId}
    <ReportLineDrawer {board} {group} tenant={newTenant} definition={null} onClose={() => (newTenant = null)} />
  {/key}
{:else if board.openedLine}
  {#key board.openedLine.id}
    <ReportLineDrawer
      {board}
      {group}
      tenant={ReportLineTenant.of(board.openedLine)}
      definition={board.openedLine}
      onClose={() => board.openGroup(group.id)}
    />
  {/key}
{/if}
{/if}

<style>
  .detail-header {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .back {
    margin-bottom: var(--space-2);
  }
  h1 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
  }
  .channels {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-top: var(--space-2);
  }
  .rows {
    display: flex;
    flex-direction: column;
  }
  .row {
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.4fr) minmax(0, 0.8fr) 120px;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-3) var(--space-2);
    border-top: 1px solid var(--border-subtle);
    border-radius: var(--radius-sm);
    text-align: left;
  }
  .row > :global(:last-child) {
    justify-self: end;
  }
  .head {
    border-top: none;
    padding-bottom: var(--space-2);
  }
  .clickable:hover {
    background: var(--bg-hover);
  }
  .paused {
    opacity: 0.6;
  }
  .who {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .name {
    color: var(--text-primary);
    font-weight: 500;
  }
  .sub {
    color: var(--text-tertiary);
    font-size: 12px;
  }
  .label {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .footnote,
  .empty {
    margin: var(--space-3) 0 0;
    color: var(--text-tertiary);
    font-size: 12px;
  }
</style>
